/**
 * Warden model (vanilla WardenModel, 128x128 sheet): wide torso with ribcage panes, a flat-topped head with
 * two tendrils, long arms, short legs. Over the base sheet come the vanilla emissive layers: the bioluminescent
 * spots, two pulsating-spot sheets that alternate, and the chest "heart" that throbs with the heartbeat.
 *
 * Driven by the view (entities/boss/warden.ts `pack()`):
 *   color bits 0-2 state (0 idle, 1 emerging, 2 digging, 3 sniffing, 4 roaring, 5 charging a sonic boom),
 *   color bits 3-7 progress 0..31 of that state, heldItem = top anger 0..150, swell = heartbeat pulse 0..1.
 * Model px space, y down, front -z, feet at y = 24 (bone pivot).
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat } from './core';
import type { OwAnim } from './overworldModels';

const PI = Math.PI;
const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number], mirror = false): CubeDef =>
  ({ tex, from, size, mirror });

/** Glow layers sit a hair outside the base so the equal-depth faces do not z-fight. */
const GLOW_INFLATE = 0.04;
/** Neutral dim for the glow sheets (vanilla emissive layers are not full white). */
const GLOW = 0xc2d2d0;

export function wardenAnim(env: EnvUniforms): OwAnim {
  const main = entityMaterial(env, sheetTexture('entity/warden/warden'), { doubleSided: true });
  const mk = (sheet: string): EntityMat => entityMaterial(env, sheetTexture(`entity/warden/${sheet}`), { emissive: true, doubleSided: true, tint: GLOW });
  const bio = mk('warden_bioluminescent_layer');
  const spots1 = mk('warden_pulsating_spots_1');
  const spots2 = mk('warden_pulsating_spots_2');
  const heart = mk('warden_heart');
  const glows = [bio, spots1, spots2];
  const mats: EntityMat[] = [main, ...glows, heart];

  const parts = new THREE.Group();
  const pivot = (pos: [number, number, number], parent: THREE.Object3D, rot: [number, number, number] = [0, 0, 0]): THREE.Group => {
    const g = new THREE.Group();
    g.rotation.order = 'ZYX';
    g.position.set(pos[0], pos[1], pos[2]);
    g.rotation.set(rot[0], rot[1], rot[2]);
    parent.add(g);
    return g;
  };
  /** Base cuboid, plus the same cuboid in every glow layer (`glow` false: ribcage panes etc.). */
  const box = (g: THREE.Object3D, c: CubeDef, glow = true, withHeart = false): void => {
    g.add(buildPart({ cubes: [c] }, 128, 128, main));
    if (!glow) return;
    const big = { ...c, inflate: GLOW_INFLATE };
    for (const m of glows) g.add(buildPart({ cubes: [big] }, 128, 128, m));
    if (withHeart) g.add(buildPart({ cubes: [big] }, 128, 128, heart));
  };

  const bone = pivot([0, 24, 0], parts);
  const body = pivot([0, -21, 0], bone);
  box(body, cube([0, 0], [-9, -13, -4], [18, 21, 11]), true, true);
  const ribR = pivot([-7, -2, -4], body), ribL = pivot([7, -2, -4], body);
  box(ribR, cube([90, 11], [-2, -11, -0.1], [9, 21, 0]), false);
  box(ribL, cube([90, 11], [-7, -11, -0.1], [9, 21, 0], true), false);
  const head = pivot([0, -13, 0], body);
  box(head, cube([0, 32], [-8, -16, -5], [16, 16, 10]));
  const tendR = pivot([-8, -12, 0], head), tendL = pivot([8, -12, 0], head);
  box(tendR, cube([52, 32], [-16, -13, 0], [16, 16, 0]));
  box(tendL, cube([58, 0], [0, -13, 0], [16, 16, 0]));
  const armR = pivot([-13, -13, 1], body), armL = pivot([13, -13, 1], body);
  box(armR, cube([44, 50], [-4, 0, -4], [8, 28, 8]));
  box(armL, cube([0, 58], [-4, 0, -4], [8, 28, 8]));
  const legR = pivot([-5.9, -13, 0], bone), legL = pivot([5.9, -13, 0], bone);
  box(legR, cube([76, 48], [-3, 0, -3], [6, 13, 6]));
  box(legL, cube([76, 76], [-3, 0, -3], [6, 13, 6]));

  const ease = (t: number): number => t * t * (3 - 2 * t);

  return {
    parts, mats,
    animate(v, q, headY, headX, age) {
      const state = v.color & 7, p = ((v.color >> 3) & 31) / 31;
      const anger = Math.min(1, (v.heldItem || 0) / 150);
      // rest pose
      bone.position.y = 24;
      body.rotation.set(0, 0, 0);
      armR.rotation.set(0, 0, 0); armL.rotation.set(0, 0, 0);
      head.rotation.set(headX, headY, 0);

      // walk: legs and arms in opposition, torso sways
      const s = q.limbSwing * 0.6662, a = Math.min(1, q.limbAmt * 1.4);
      legR.rotation.set(Math.cos(s) * 1.1 * a, 0, 0); legL.rotation.set(Math.cos(s + PI) * 1.1 * a, 0, 0);
      armR.rotation.x = Math.cos(s + PI) * 0.7 * a; armL.rotation.x = Math.cos(s) * 0.7 * a;
      body.rotation.z = Math.sin(s) * 0.06 * a;
      // idle sway
      armR.rotation.z = -0.05 + Math.sin(age * 0.1) * 0.03; armL.rotation.z = 0.05 - Math.sin(age * 0.1) * 0.03;

      // tendrils wave faster and wider the angrier it is (vanilla tendrilAnimation)
      const tend = (0.25 + 0.75 * Math.max(anger, state === 5 ? 1 : 0)) * Math.cos(age * 2.25) * PI * 0.1;
      tendL.rotation.set(tend, 0, 0); tendR.rotation.set(-tend, 0, 0);

      let drop = 0;
      if (v.attackAnim > 0) {
        const sw = Math.sin((1 - v.attackAnim) * PI);
        armR.rotation.x = armL.rotation.x = -2.0 * sw;
        body.rotation.x = 0.2 * sw;
      }
      switch (state) {
        case 1: // rising out of the ground, arms thrown up
          drop = (1 - ease(p)) * 52;
          armR.rotation.x = armL.rotation.x = -2.4 * (1 - p) * (1 - p);
          body.rotation.x = -0.3 * (1 - p);
          break;
        case 2: // sinking back
          drop = ease(p) * 52;
          armR.rotation.x = armL.rotation.x = -1.2 * p;
          break;
        case 3: // sniffing: nose down, bobbing
          head.rotation.x = headX + 0.55 + 0.18 * Math.sin(age * 0.6);
          body.rotation.x = 0.12;
          break;
        case 4: { // roar: head back, arms out, shaking
          const r = Math.sin(Math.min(1, p * 1.2) * PI);
          head.rotation.x = headX - 0.7 * r;
          body.rotation.x = -0.22 * r;
          armR.rotation.x = armL.rotation.x = -0.9 * r;
          armR.rotation.z = -0.5 * r; armL.rotation.z = 0.5 * r;
          bone.position.x = Math.sin(age * 2.1) * 0.25 * r;
          break;
        }
        case 5: // sonic charge: rears back, arms apart
          head.rotation.x = headX - 0.45 * p;
          body.rotation.x = -0.2 * p;
          armR.rotation.z = -0.35 * p; armL.rotation.z = 0.35 * p;
          break;
      }
      if (state !== 4) bone.position.x = 0;
      bone.position.y = 24 + drop;

      // glow: heart throbs with the beat; the spot sheets trade places; both brighten with anger
      const k = 0.7 + 0.3 * anger;
      const throb = 0.55 + 0.6 * (v.swell || 0) + (state === 5 ? 0.5 * p : 0);
      heart.uniforms.uTint.value.setScalar(throb);
      const pulse = 0.5 + 0.5 * Math.sin(age * (0.12 + 0.12 * anger));
      spots1.uniforms.uTint.value.setScalar(k * (0.35 + 0.65 * pulse));
      spots2.uniforms.uTint.value.setScalar(k * (0.35 + 0.65 * (1 - pulse)));
      bio.uniforms.uTint.value.setScalar(k * 0.8);
    },
    dispose() { for (const m of mats) m.dispose(); },
  };
}
